import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

import { parseUtcDate } from "./dateUtils"

export { formatFileSize } from "./utils/format-file-size"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a date into a human-readable string
 * @param date The date to format
 * @returns A formatted date string
 */
export function formatDate(date: Date): string {
  if (!date || isNaN(date.getTime())) {
    return ""
  }

  // Format as: "Jan 1, 2023"
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format(date)
}

/**
 * Normalize string for search: lowercase, strip diacritics and leading '@'.
 */
export function normalizeForSearch(value: string): string {
  return value
    .toLowerCase()
    .replace(/^@+/, "")
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
}

/**
 * Checks if a value matches a query using normalized substring match.
 */
export function matchesSearch(value: string | null | undefined, query: string): boolean {
  const q = query.trim()
  if (!q) return true
  const hay = normalizeForSearch(value ?? "")
  const needle = normalizeForSearch(q)
  return hay.includes(needle)
}

/**
 * Format a UTC timestamp string into a relative time string
 * Uses the same translation keys as automated messages for consistency
 * @param utcTimestamp - UTC timestamp string from API
 * @param t - i18n translation function
 * @returns Relative time string (localized via i18n)
 */
export function formatRelativeTime(
  utcTimestamp: string,
  t: (key: string, fallback: string, params?: any) => string
): string {
  try {
    const date = parseUtcDate(utcTimestamp)
    if (!date) return ""

    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    // Less than 1 minute
    if (diffMinutes < 1) {
      return t("auto_messages-last-sent-just-now", "Just now")
    }

    // Less than 1 hour
    if (diffHours < 1) {
      if (diffMinutes === 1) {
        return t("auto_messages-last-sent-minute", "1 minute ago")
      }
      return t("auto_messages-last-sent-minutes", "{{minutes}} minutes ago", { minutes: diffMinutes })
    }

    // Less than 24 hours
    if (diffHours < 24) {
      if (diffHours === 1) {
        return t("auto_messages-last-sent-hour", "1 hour ago")
      }
      return t("auto_messages-last-sent-hours", "{{hours}} hours ago", { hours: diffHours })
    }

    // Less than 7 days
    if (diffDays < 7) {
      if (diffDays === 1) {
        return t("auto_messages-last-sent-yesterday", "Yesterday")
      }
      return t("auto_messages-last-sent-days", "{{days}} days ago", { days: diffDays })
    }

    // 7+ days - use absolute date formatting
    return formatDate(date)
  } catch {
    return ""
  }
}

/**
 * Check if a UTC timestamp is within the last 24 hours
 * @param utcTimestamp - UTC timestamp string (e.g., "2024-01-15T10:30:00" or "2024-01-15T10:30:00Z")
 * @returns true if the timestamp is less than 24 hours old
 */
export function isRecent(utcTimestamp: string): boolean {
  try {
    const date = parseUtcDate(utcTimestamp)
    if (!date) return false

    const now = new Date()
    const hoursDiff = (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    return hoursDiff < 24
  } catch {
    return false
  }
}

/**
 * Deep clone an object using structuredClone.
 */
export function deepClone<T>(obj: T): T {
  return structuredClone(obj)
}
